import { getUnifiedClient, appConfig } from '@kidslabs/module-core';
import { type ApolloClient, type NormalizedCacheObject } from '@apollo/client';
import type { CartItemReference, CartItemReferenceLogic, CartItemReferenceAction } from '../models';
import { LogicType } from '../models/types/cart-item-reference-logic';
import { ActionType } from '../models/types/cart-item-reference-action';
import {
    CartSyncReferencesDocument,
    type CartSyncReferencesQuery,
    type CartSyncReferencesQueryVariables,
    ActionType as ServerActionType,
    type ProductInterface,
} from '../types/front.graphql';
import type { ProductModel } from '@kidslabs/module-catalog';

/**
 * ============================================================================
 * CART SYNC REFERENCES SERVICE
 * ============================================================================
 *
 * This service handles synchronization of cart item references from the server
 * using the `cartSyncReferences` GraphQL API.
 *
 * ARCHITECTURE PRINCIPLE:
 * - Server is the source of truth for cart item references
 * - This service fetches server data and transforms it to local models
 * - NO local reconciliation needed - server data is authoritative
 *
 * WHY THIS SERVICE IS NEEDED:
 * - Previous approach: Fetch cart items → reconcile with local references
 *   Problem: Local reconciliation can be inaccurate and causes data inconsistency
 *
 * - New approach: Fetch cartSyncReferences → transform to local models directly
 *   Benefit: Server-authoritative data, no reconciliation complexity
 *
 * USAGE:
 * ```typescript
 * const service = new CartSyncReferencesService();
 * const result = await service.fetchAndTransform(cartId);
 * if (result.success) {
 *   // Update local store with result.references
 * }
 * ```
 */

// ============================================================================
// TYPES
// ============================================================================

/**
 * Server response type from cartSyncReferences query
 */
type ServerCartItemReference = NonNullable<NonNullable<CartSyncReferencesQuery['cartSyncReferences']>[number]>;

/**
 * Server logic type from cartSyncReferences query
 */
type ServerLogic = NonNullable<NonNullable<ServerCartItemReference['logics']>[number]>;

/**
 * Server action type from cartSyncReferences query
 */
type ServerAction = NonNullable<NonNullable<ServerLogic['actions']>[number]>;

/**
 * Result of fetch and transform operation
 */
export interface CartSyncReferencesResult {
    success: boolean;
    references: CartItemReference[];
    error?: Error;
    stats: CartSyncReferencesStats;
}

/**
 * Statistics for debugging and logging
 */
export interface CartSyncReferencesStats {
    totalReferences: number;
    selectedReferences: number;
    referencesWithLogics: number;
    totalCartItems: number;
}

// ============================================================================
// SERVICE
// ============================================================================

export class CartSyncReferencesService {
    private client: ApolloClient<NormalizedCacheObject> | undefined = undefined;

    /**
     * Get Apollo client instance (lazy initialization)
     */
    private async getClient(): Promise<ApolloClient<NormalizedCacheObject> | undefined> {
        if (!this.client) {
            this.client = await getUnifiedClient();
        }
        return this.client;
    }

    /**
     * Fetch cart sync references from server and transform to local models
     *
     * @param cartId - The cart ID to fetch references for
     * @returns CartSyncReferencesResult with transformed references
     */
    async fetchAndTransform(cartId: string): Promise<CartSyncReferencesResult> {
        const stats: CartSyncReferencesStats = {
            totalReferences: 0,
            selectedReferences: 0,
            referencesWithLogics: 0,
            totalCartItems: 0,
        };

        try {
            console.log('[CartSyncReferencesService] Fetching cart sync references', { cartId });

            const client = await this.getClient();
            if (!client) {
                console.error('[CartSyncReferencesService] Failed to get Apollo client');
                return {
                    success: false,
                    references: [],
                    error: new Error('Failed to initialize Apollo client'),
                    stats,
                };
            }

            const { data, error } = await client.query<CartSyncReferencesQuery, CartSyncReferencesQueryVariables>({
                query: CartSyncReferencesDocument,
                variables: { cartId },
                fetchPolicy: 'network-only',
                context: {
                    uri: appConfig.magentoGraphQlUrl,
                },
            });

            if (error) {
                console.error('[CartSyncReferencesService] GraphQL error', { error });
                return {
                    success: false,
                    references: [],
                    error: new Error(error.message),
                    stats,
                };
            }

            const serverReferences = data?.cartSyncReferences;
            console.log('[CartSyncReferencesService] serverReferences before tranform: ', {
                serverReferences: data?.cartSyncReferences,
            });

            if (!serverReferences || serverReferences.length === 0) {
                console.log('[CartSyncReferencesService] No references returned from server');
                return {
                    success: true,
                    references: [],
                    stats,
                };
            }

            // Transform server references to local models
            const localReferences: CartItemReference[] = [];

            for (const serverRef of serverReferences) {
                if (!serverRef) continue;

                const localRef = this.transformServerReferenceToLocal(serverRef, cartId);
                if (localRef) {
                    localReferences.push(localRef);

                    // Update stats
                    stats.totalReferences++;
                    if (localRef.isSelected) stats.selectedReferences++;
                    if (localRef.logics && localRef.logics.length > 0) stats.referencesWithLogics++;
                    if (localRef.cartItem) stats.totalCartItems += 1;
                }
            }

            // Normalize prices for references that share the same Magento cart item.
            // Magento consolidates the same product into one cart item (e.g. qty=17, row_total for all 17),
            // but the marketing service splits it into multiple references with different promotion logics.
            // Without normalization, each reference would show the full Magento row_total, causing doubled prices.
            const normalizedReferences = this.normalizeSharedCartItemPrices(localReferences);

            console.log('[CartSyncReferencesService] Successfully transformed references', {
                stats: stats,
                serverReferences: serverReferences,
                localReferences: normalizedReferences,
            });

            return {
                success: true,
                references: normalizedReferences,
                stats,
            };
        } catch (error) {
            console.error('[CartSyncReferencesService] Error fetching cart sync references', { error });
            return {
                success: false,
                references: [],
                error: error instanceof Error ? error : new Error(String(error)),
                stats,
            };
        }
    }

    /**
     * Transform a single server reference to local CartItemReference model
     */
    private transformServerReferenceToLocal(
        serverRef: ServerCartItemReference,
        cartId: string
    ): CartItemReference | null {
        try {
            // Use product_id directly from server (authoritative)
            const firstCartItem = serverRef.cart_items?.[0];
            const productId = serverRef.product_id ?? firstCartItem?.product?.id ?? 0;

            if (productId === 0) {
                console.warn('[CartSyncReferencesService] Could not extract productId from server reference', {
                    referenceId: serverRef.cart_item_reference_id,
                    sku: serverRef.sku,
                });
            }

            // Transform logics
            const logics = this.transformLogics(serverRef.logics, serverRef.cart_item_reference_id);

            // Transform cart item (take first from server array - 1:1 relationship)
            const cartItems = this.transformCartItems(serverRef.cart_items);
            const cartItem = cartItems?.[0] ?? undefined;

            // Transform parent product if available
            const parentProduct = this.transformParentProduct(serverRef.parent_product);

            // Determine cartItemId from cart_item_id (server) or first cart item's id
            const cartItemId = serverRef.cart_item_id?.toString() ?? firstCartItem?.id ?? undefined;

            const localRef: CartItemReference = {
                cartItemReferenceId: serverRef.cart_item_reference_id,
                productId,
                cartId: serverRef.cart_id ?? cartId,
                cartItemId,
                qty: serverRef.qty,
                logics,
                isSelected: serverRef.is_selected ?? false,
                createdAt: serverRef.created_at ?? new Date().toISOString(),
                cartItem: cartItem as unknown as CartItemReference['cartItem'],
                parentProduct,
                sku: serverRef.sku, // Preserve variant SKU for configurable, product SKU for simple
            };

            return localRef;
        } catch (error) {
            console.error('[CartSyncReferencesService] Error transforming server reference', {
                referenceId: serverRef.cart_item_reference_id,
                error,
            });
            return null;
        }
    }

    /**
     * Transform server logics to local CartItemReferenceLogic models
     */
    private transformLogics(
        serverLogics: ServerCartItemReference['logics'],
        cartItemReferenceId: string
    ): CartItemReferenceLogic[] {
        if (!serverLogics || serverLogics.length === 0) {
            return [];
        }

        const localLogics: CartItemReferenceLogic[] = [];

        for (const serverLogic of serverLogics) {
            if (!serverLogic) continue;

            const localLogic = this.transformSingleLogic(serverLogic, cartItemReferenceId);
            if (localLogic) {
                localLogics.push(localLogic);
            }
        }

        return localLogics;
    }

    /**
     * Transform a single server logic to local CartItemReferenceLogic model
     */
    private transformSingleLogic(serverLogic: ServerLogic, cartItemReferenceId: string): CartItemReferenceLogic | null {
        try {
            // Transform actions
            const actions = this.transformActions(serverLogic.actions, serverLogic.logic_id);

            const localLogic: CartItemReferenceLogic = {
                id: serverLogic.id ?? serverLogic.logic_id,
                cartItemReferenceId: serverLogic.cart_item_reference_id ?? cartItemReferenceId,
                logicType: mapServerLogicType(serverLogic.logic_type),
                logicId: parseInt(serverLogic.logic_id, 10) || 0,
                logicName: serverLogic.logic_name ?? undefined,
                actions,
                sessionId: serverLogic.session_id ?? undefined,
                allocatedQty: serverLogic.allocated_qty ?? undefined,
                createdAt: new Date().toISOString(),
            };

            return localLogic;
        } catch (error) {
            console.error('[CartSyncReferencesService] Error transforming logic', {
                logicId: serverLogic.logic_id,
                error,
            });
            return null;
        }
    }

    /**
     * Transform server actions to local CartItemReferenceAction models
     */
    private transformActions(serverActions: ServerLogic['actions'], logicId: string): CartItemReferenceAction[] {
        if (!serverActions || serverActions.length === 0) {
            return [];
        }

        const localActions: CartItemReferenceAction[] = [];

        for (const serverAction of serverActions) {
            if (!serverAction) continue;

            const localAction = this.transformSingleAction(serverAction, logicId);
            if (localAction) {
                localActions.push(localAction);
            }
        }

        return localActions;
    }

    /**
     * Transform a single server action to local CartItemReferenceAction model
     */
    private transformSingleAction(serverAction: ServerAction, logicId: string): CartItemReferenceAction | null {
        try {
            const localAction: CartItemReferenceAction = {
                id: serverAction.id ?? serverAction.result_quote_item_id?.toString(),
                cartItemReferenceLogicId: serverAction.cart_item_reference_logic_id ?? logicId,
                actionType: mapServerActionType(serverAction.action_type),
                actionProduct: (serverAction.action_product as ProductInterface) ?? undefined,
                actionValue: serverAction.action_value ?? null,
                actionNote: serverAction.action_note ?? null,
                actionQty: serverAction.action_qty ?? null,
                createdAt: new Date().toISOString(),
            };

            return localAction;
        } catch (error) {
            console.error('[CartSyncReferencesService] Error transforming action', {
                resultQuoteItemId: serverAction.result_quote_item_id,
                error,
            });
            return null;
        }
    }

    /**
     * Transform server cart items to local cart item format
     * These are stored in CartItemReference.cartItem for backward compatibility
     */
    private transformCartItems(
        serverCartItems: ServerCartItemReference['cart_items']
    ): ServerCartItemReference['cart_items'] {
        // Return as-is since the server already provides the correct format
        // The cart_items from cartSyncReferences are already in the correct structure
        return serverCartItems ?? [];
    }

    /**
     * Transform server parent product to local ProductModel
     */
    private transformParentProduct(
        serverParentProduct: ServerCartItemReference['parent_product']
    ): ProductModel | undefined {
        if (!serverParentProduct) {
            return undefined;
        }

        // The parent_product from server has ProductInterface structure
        // Cast to ProductModel for local use
        return serverParentProduct as unknown as ProductModel;
    }

    // ============================================================================
    // SHARED CART ITEM PRICE NORMALIZATION
    // ============================================================================

    /**
     * Normalize prices when multiple CartItemReferences share the same Magento cart item.
     *
     * PROBLEM:
     * Magento consolidates the same product into a single cart item (e.g., cart_item_id=7474002, qty=17, row_total=5,996,750).
     * The marketing service splits this into multiple references with different promotion logics (e.g., ref1 qty=11, ref2 qty=6).
     * Both references carry the SAME cart_items[0] from Magento with quantity=17 and row_total=5,996,750.
     * Without normalization, the UI displays the full row_total for EACH reference, effectively doubling the price.
     *
     * SOLUTION:
     * Group references by cartItemId. For groups with >1 reference sharing the same cart item,
     * recalculate each reference's cartItem.quantity and cartItem.prices proportionally based on reference.qty.
     *
     * @param references - Transformed local references (may have shared cart items)
     * @returns References with normalized per-reference prices
     */
    private normalizeSharedCartItemPrices(references: CartItemReference[]): CartItemReference[] {
        // Group references by cartItemId to detect shared Magento cart items
        const referencesByCartItemId = new Map<string, CartItemReference[]>();

        for (const ref of references) {
            if (!ref.cartItemId || !ref.cartItem) {
                continue;
            }
            const group = referencesByCartItemId.get(ref.cartItemId) ?? [];
            group.push(ref);
            referencesByCartItemId.set(ref.cartItemId, group);
        }

        // Find groups that need normalization (>1 reference sharing the same cart item)
        const sharedCartItemIds = new Set<string>();
        for (const [cartItemId, group] of referencesByCartItemId) {
            if (group.length > 1) {
                sharedCartItemIds.add(cartItemId);
            }
        }

        // No shared cart items — return as-is (most common path, zero overhead)
        if (sharedCartItemIds.size === 0) {
            return references;
        }

        console.log('[CartSyncReferencesService] Normalizing shared cart item prices', {
            sharedCartItemIds: Array.from(sharedCartItemIds),
            affectedGroups: Array.from(sharedCartItemIds).map(id => ({
                cartItemId: id,
                referenceCount: referencesByCartItemId.get(id)?.length ?? 0,
            })),
        });

        // Normalize prices for each shared group
        return references.map(ref => {
            if (!ref.cartItemId || !ref.cartItem || !sharedCartItemIds.has(ref.cartItemId)) {
                return ref; // Not shared — no changes needed
            }

            return this.recalculateCartItemPricesForReference(ref);
        });
    }

    /**
     * Recalculate a reference's cartItem prices based on the reference's qty.
     *
     * Uses unit price (cartItem.prices.price.value) as the source of truth since it is
     * always correct per-unit. Derives row_total and other aggregate fields from it.
     *
     * Uses proportional ratio (ref.qty / cartItem.quantity) as fallback for fields
     * where unit-level calculation is not applicable (e.g., discounts, tax-inclusive totals).
     *
     * @param ref - The CartItemReference to normalize
     * @returns A new CartItemReference with recalculated cartItem prices
     */
    private recalculateCartItemPricesForReference(ref: CartItemReference): CartItemReference {
        const cartItem = ref.cartItem;
        if (!cartItem?.prices) {
            return ref;
        }

        const referenceQty = ref.qty;
        const magentoQty = cartItem.quantity ?? 0;

        // Safety: skip normalization if data is invalid
        if (referenceQty <= 0 || magentoQty <= 0 || referenceQty === magentoQty) {
            return ref;
        }

        const prices = cartItem.prices;
        const unitPrice = prices.price?.value ?? 0;

        // Skip if unit price is zero or missing (cannot calculate)
        if (unitPrice <= 0) {
            return ref;
        }

        // Proportional ratio for fields that cannot be derived from unit price alone
        const proportionalRatio = referenceQty / magentoQty;

        // Recalculate row_total: unit_price × reference_qty (most accurate for VND integer currency)
        const normalizedRowTotal = Math.round(unitPrice * referenceQty);

        // Recalculate row_total_including_tax proportionally from original
        const originalRowTotalInclTax = prices.row_total_including_tax?.value ?? 0;
        const normalizedRowTotalInclTax = Math.round(originalRowTotalInclTax * proportionalRatio);

        // Recalculate total_item_discount proportionally
        const originalTotalDiscount = prices.total_item_discount?.value ?? 0;
        const normalizedTotalDiscount = Math.round(originalTotalDiscount * proportionalRatio);

        // Recalculate discounts array proportionally
        const normalizedDiscounts =
            prices.discounts?.map(discount => {
                if (!discount) return discount;
                const originalDiscountAmount = discount.amount?.value ?? 0;
                return {
                    ...discount,
                    amount: {
                        ...discount.amount,
                        value: Math.round(originalDiscountAmount * proportionalRatio),
                    },
                };
            }) ?? null;

        console.log('[CartSyncReferencesService] Normalized prices for reference', {
            referenceId: ref.cartItemReferenceId,
            cartItemId: ref.cartItemId,
            referenceQty,
            magentoQty,
            unitPrice,
            originalRowTotal: prices.row_total?.value,
            normalizedRowTotal,
            originalRowTotalInclTax,
            normalizedRowTotalInclTax,
        });

        // Create a new cartItem with normalized prices and quantity (immutable update)
        const normalizedCartItem = {
            ...cartItem,
            quantity: referenceQty,
            prices: {
                ...prices,
                // price (unit price) stays unchanged — it is already correct per-unit
                row_total: {
                    ...prices.row_total,
                    value: normalizedRowTotal,
                },
                row_total_including_tax: {
                    ...prices.row_total_including_tax,
                    value: normalizedRowTotalInclTax,
                },
                total_item_discount: prices.total_item_discount
                    ? {
                          ...prices.total_item_discount,
                          value: normalizedTotalDiscount,
                      }
                    : null,
                discounts: normalizedDiscounts,
            },
        };

        return {
            ...ref,
            cartItem: normalizedCartItem as CartItemReference['cartItem'],
        };
    }
}

// ============================================================================
// SERVER → LOCAL ENUM MAPPERS
// ============================================================================

const SERVER_ACTION_TYPE_MAP: Record<ServerActionType, ActionType> = {
    [ServerActionType.FreeGift]: ActionType.GIFT,
    [ServerActionType.Discount]: ActionType.DISCOUNT,
    [ServerActionType.FreeShip]: ActionType.FREE_SHIP,
    [ServerActionType.Point]: ActionType.POINT,
    [ServerActionType.Voucher]: ActionType.VOUCHER,
    [ServerActionType.Other]: ActionType.OTHER,
};

function mapServerActionType(serverType: ServerActionType | null | undefined): ActionType {
    if (!serverType) return ActionType.GIFT;
    return SERVER_ACTION_TYPE_MAP[serverType] ?? ActionType.OTHER;
}

const LOGIC_TYPE_MAP: Record<string, LogicType> = {
    promotion: LogicType.PROMOTION,
    freeship: LogicType.FREESHIP,
    campaign: LogicType.CAMPAIGN,
    other: LogicType.OTHER,
};

function mapServerLogicType(serverType: string): LogicType {
    return LOGIC_TYPE_MAP[serverType.toLowerCase()] ?? LogicType.OTHER;
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

/**
 * Singleton instance for easy access
 */
export const cartSyncReferencesService = new CartSyncReferencesService();

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Convenience function to fetch and transform cart sync references
 *
 * @param cartId - The cart ID to fetch references for
 * @returns CartSyncReferencesResult with transformed references
 */
export async function fetchCartSyncReferences(cartId: string): Promise<CartSyncReferencesResult> {
    return cartSyncReferencesService.fetchAndTransform(cartId);
}

/**
 * Reconcile server references with local state
 *
 * This function takes the authoritative server data and merges it with
 * any local-only state that should be preserved (e.g., pending changes).
 *
 * @param serverReferences - References from server (source of truth)
 * @param localReferences - Current local references (may have pending changes)
 * @param cartId - Current cart ID
 * @returns Reconciled references
 */
export function reconcileWithServerReferences(
    serverReferences: CartItemReference[],
    localReferences: CartItemReference[],
    cartId: string
): CartItemReference[] {
    console.log('[reconcileWithServerReferences] Starting rsync-like reconciliation', {
        serverCount: serverReferences.length,
        localCount: localReferences.length,
        cartId,
    });

    const localRefMap = new Map<string, CartItemReference>();
    for (const localRef of localReferences) {
        localRefMap.set(localRef.cartItemReferenceId, localRef);
    }

    // Track which local references were matched by server
    const matchedLocalIds = new Set<string>();

    const reconciledReferences: CartItemReference[] = [];

    // Phase 1: Process server references (source of truth for synced items)
    for (const serverRef of serverReferences) {
        const localRef = localRefMap.get(serverRef.cartItemReferenceId);

        if (localRef) {
            // Merge: Server data takes precedence, but preserve local-only properties
            const mergedRef: CartItemReference = {
                ...serverRef,
                createdAt: localRef.createdAt || serverRef.createdAt,
                parentCartItemReferenceId: localRef.parentCartItemReferenceId,
                // Ensure sku from server is always preserved (it's the variant SKU)
                sku: serverRef.sku ?? localRef.sku,
                // Preserve cartItem: use server data when available, fallback to local.
                // Server may not include cart_items in some edge cases (timing, race conditions).
                // Without this fallback, items lose their cartItem and get filtered out by setItems(),
                // causing them to disappear from the mini-cart and cart page.
                cartItem: serverRef.cartItem ?? localRef.cartItem,
                // Preserve parentProduct for configurable products
                parentProduct: serverRef.parentProduct ?? localRef.parentProduct,
                // Preserve local isSelected when server doesn't include it
                isSelected: serverRef.isSelected ?? localRef.isSelected,
            };
            reconciledReferences.push(mergedRef);
            matchedLocalIds.add(localRef.cartItemReferenceId);
        } else {
            // New reference from server, add as-is
            reconciledReferences.push(serverRef);
        }
    }

    // Phase 2: Preserve ALL local-only items that weren't matched by server.
    // Reconciliation only merges by cartItemReferenceId — it never deletes.
    // Items are only removed by explicit user action (remove from cart).
    // This ensures:
    //   - Guest refs survive cart ID switch (login merge)
    //   - Locally added items pending sync are preserved
    //   - Items not yet on server (new additions) are kept
    let preservedCount = 0;
    for (const localRef of localReferences) {
        if (matchedLocalIds.has(localRef.cartItemReferenceId)) {
            continue; // Already merged with server data
        }

        reconciledReferences.push(localRef);
        preservedCount++;
    }

    console.log('[reconcileWithServerReferences] Reconciliation complete', {
        serverItems: serverReferences.length,
        mergedItems: matchedLocalIds.size,
        preservedLocalOnly: preservedCount,
        finalCount: reconciledReferences.length,
    });

    return reconciledReferences;
}
